import { Alert, Skeleton } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import type { Chat } from '@trinity/chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient';
import { AttachmentContext } from './context';
import { Table } from './Table';

type Props = {
  attachment: Chat.Attachment.Table;
};

/** Table attachment whose data field is guaranteed to be present and non-null. */
type TableWithData = Chat.Attachment.TableInline;

export const TableDataContainer = ({ attachment }: Props) => {
  const isFileBacked = !!attachment.fileUrl && attachment.data == null;
  const apiClient = useChatApiClient();

  const {
    data: fetchedData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['attachment-data', attachment.id, attachment.fileUrl],
    queryFn: async () => {
      if (!attachment.fileUrl) {
        throw new Error('fileUrl is required for file-backed attachment fetch');
      }
      const res = await apiClient.authenticatedClient.get<Record<string, unknown>>(attachment.fileUrl);
      const json = res.data;
      // Handle both formats:
      // - Raw data: { headers, rows, title, ... }
      // - Full attachment object: { id, type, data: { headers, rows, ... } }
      return json.type === 'table' && json.data !== null && typeof json.data === 'object'
          ? (json.data as Chat.Attachment.TableData)
          : (json as Chat.Attachment.TableData);
    },
    enabled: isFileBacked,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Prefer local data when available. Direct property check ensures data is non-null;
  // cast to TableWithData because TS doesn't narrow parent objects through optional property checks.
  if (attachment.data != null) {
    const withData = attachment as TableWithData;
    return (
      <AttachmentContext.Provider value={withData}>
        <Table item={withData} />
      </AttachmentContext.Provider>
    );
  }

  // Fall back to proxy fetch when local data is not available
  if (isFileBacked) {
    if (isLoading) return <Skeleton variant="rectangular" height={200} />;
    if (isError || !fetchedData) return <Alert severity="error">Failed to load table data.</Alert>;
    // Spread + property override doesn't produce an intersection type in TypeScript,
    // so we assert. The cast is safe: attachment is Table, fetchedData is NonNull.
    const resolved = { ...attachment, data: fetchedData } as TableWithData;
    return (
      <AttachmentContext.Provider value={resolved}>
        <Table item={resolved} />
      </AttachmentContext.Provider>
    );
  }

  return null;
};

TableDataContainer.displayName = 'Chat.Attachments.TableDataContainer';
